// Multi-currency conversion service
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export type CurrencyCode = 'INR' | 'USD' | 'EUR' | 'GBP' | 'AED' | 'SAR' | 'SGD' | 'AUD';

export interface CurrencyInfo {
  code: CurrencyCode;
  symbol: string;
  name: string;
}

export interface CurrencyRates {
  base: string;
  rates: Record<string, number>;
  updated_at: string;
  // Legacy compat
  USD_TO_INR: number;
  last_updated: string;
  source: string;
}

export interface ConversionResult {
  originalAmount: number;
  originalCurrency: string;
  convertedAmount: number;
  convertedCurrency: string;
  exchangeRate: number;
  formattedOriginal: string;
  formattedConverted: string;
}

const CURRENCY_META: Record<CurrencyCode, CurrencyInfo> = {
  INR: { code: 'INR', symbol: '\u20B9', name: 'Indian Rupee' },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar' },
  EUR: { code: 'EUR', symbol: '\u20AC', name: 'Euro' },
  GBP: { code: 'GBP', symbol: '\u00A3', name: 'British Pound' },
  AED: { code: 'AED', symbol: '\u062F.\u0625', name: 'UAE Dirham' },
  SAR: { code: 'SAR', symbol: '\uFDFC', name: 'Saudi Riyal' },
  SGD: { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  AUD: { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
};

// Fallback offline rates (INR base)
const FALLBACK_RATES: Record<CurrencyCode, number> = {
  INR: 1,
  USD: 0.01195,
  EUR: 0.01102,
  GBP: 0.00946,
  AED: 0.04389,
  SAR: 0.04482,
  SGD: 0.01607,
  AUD: 0.01843,
};

class CurrencyService {
  private static instance: CurrencyService;
  private cachedRates: Record<string, number> = { ...FALLBACK_RATES };
  private baseCurrency: CurrencyCode = 'INR';
  private lastUpdated: Date = new Date();
  private updateInterval: NodeJS.Timeout | null = null;

  static getInstance(): CurrencyService {
    if (!CurrencyService.instance) {
      CurrencyService.instance = new CurrencyService();
    }
    return CurrencyService.instance;
  }

  /** Fetch live rates from the backend */
  async fetchRates(base: CurrencyCode = 'INR'): Promise<Record<string, number>> {
    try {
      const res = await fetch(`${BASE_URL}/api/v1/currency/rates?base=${base}`);
      if (res.ok) {
        const data = await res.json();
        this.cachedRates = { [base]: 1, ...data.rates };
        this.baseCurrency = base;
        this.lastUpdated = new Date();
      }
    } catch {
      // Use fallback rates
    }
    return this.cachedRates;
  }

  /** Convert between any two supported currencies */
  async convert(amount: number, from: CurrencyCode, to: CurrencyCode): Promise<ConversionResult> {
    if (from === to) {
      return {
        originalAmount: amount,
        originalCurrency: from,
        convertedAmount: amount,
        convertedCurrency: to,
        exchangeRate: 1,
        formattedOriginal: this.formatCurrency(amount, from),
        formattedConverted: this.formatCurrency(amount, to),
      };
    }

    try {
      const res = await fetch(`${BASE_URL}/api/v1/currency/convert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, from_currency: from, to_currency: to }),
      });
      if (res.ok) {
        const data = await res.json();
        return {
          originalAmount: amount,
          originalCurrency: from,
          convertedAmount: data.converted_amount,
          convertedCurrency: to,
          exchangeRate: data.rate,
          formattedOriginal: this.formatCurrency(amount, from),
          formattedConverted: this.formatCurrency(data.converted_amount, to),
        };
      }
    } catch {
      // Fall back to local rates
    }

    // Local fallback
    const rate = (FALLBACK_RATES[to] || 1) / (FALLBACK_RATES[from] || 1);
    const converted = amount * rate;
    return {
      originalAmount: amount,
      originalCurrency: from,
      convertedAmount: converted,
      convertedCurrency: to,
      exchangeRate: rate,
      formattedOriginal: this.formatCurrency(amount, from),
      formattedConverted: this.formatCurrency(converted, to),
    };
  }

  // Legacy helpers
  async getCurrentRate(): Promise<number> {
    await this.fetchRates('INR');
    return 1 / (this.cachedRates['USD'] || 0.01195); // USD to INR
  }

  async convertUSDToINR(usdAmount: number): Promise<ConversionResult> {
    return this.convert(usdAmount, 'USD', 'INR');
  }

  async convertINRToUSD(inrAmount: number): Promise<ConversionResult> {
    return this.convert(inrAmount, 'INR', 'USD');
  }

  formatCurrency(amount: number, currency: CurrencyCode | string): string {
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      const sym = CURRENCY_META[currency as CurrencyCode]?.symbol || currency;
      return `${sym}${amount.toFixed(2)}`;
    }
  }

  getCurrencyInfo(): CurrencyRates {
    const usdToInr = 1 / (FALLBACK_RATES['USD'] || 0.01195);
    return {
      base: this.baseCurrency,
      rates: this.cachedRates,
      updated_at: this.lastUpdated.toISOString(),
      USD_TO_INR: usdToInr,
      last_updated: this.lastUpdated.toISOString(),
      source: 'AI Commerce OS Currency Service',
    };
  }

  getSupportedCurrencies(): CurrencyInfo[] {
    return Object.values(CURRENCY_META);
  }

  async convertMultiple(
    amounts: Array<{ amount: number; from: CurrencyCode; to: CurrencyCode }>
  ): Promise<ConversionResult[]> {
    return Promise.all(amounts.map((item) => this.convert(item.amount, item.from, item.to)));
  }

  async getDisplayPrice(amount: number, from: CurrencyCode = 'USD', to: CurrencyCode = 'INR'): Promise<string> {
    const result = await this.convert(amount, from, to);
    return `${result.formattedOriginal} (${result.formattedConverted})`;
  }

  startAutoUpdate(intervalMinutes: number = 30): void {
    if (this.updateInterval) clearInterval(this.updateInterval);
    this.updateInterval = setInterval(() => this.fetchRates(), intervalMinutes * 60 * 1000);
  }

  stopAutoUpdate(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }
}

export const currencyService = CurrencyService.getInstance();
export default currencyService;
